import cx from 'classnames'
import { keyBy, map, uniq } from 'lodash/fp'
import memoize from 'memoize-one'
import {
  MonitorCulturePlate,
  MonitorParsedWorklist,
} from '~/api/backend/pages/wellPageAPI'
import {
  RecordManualPassageCulturePlateResponse,
  RecordManualPassageParsedWorklist,
} from '~/api/operatorActions/recordManualPassage'
import RightPointerIcon from '~/components/icons/RightPointerIcon'
import Table, { TableColumn } from '~/components/Table'
import TinyMicroplate from '~/components/TinyMicroplate'
import { convertWellCoordsToWellName } from '~/utils/microplate'
import cs from './record_manual_passage_worklist_preview.scss'

export interface RecordManualPassageWorklistPreviewMonitorProps {
  className?: string
  worklists: RecordManualPassageParsedWorklist[] | MonitorParsedWorklist[]
  plates: RecordManualPassageCulturePlateResponse[] | MonitorCulturePlate[]
}

interface RecordManualPassagePreviewWorklistMonitorProps {
  parentPlateBarcode: string
  plateBarcode: string
  parentWells: string[]
  wells: string[]
}

export const getPreviewWorklists = (
  worklists: MonitorParsedWorklist[],
): RecordManualPassagePreviewWorklistMonitorProps[] => {
  return map((worklist: MonitorParsedWorklist) => {
    return {
      id: `${worklist.parent_plate_barcode}__${worklist.plate_barcode}`,
      parentPlateBarcode: worklist.parent_plate_barcode,
      plateBarcode: worklist.plate_barcode,
      parentWells: uniq(worklist.well_transfers.map(transfer => transfer.parent_well)),
      wells: uniq(worklist.well_transfers.map(transfer => transfer.well)),
    }
  }, worklists)
}

const _getPreviewWorklists = memoize(getPreviewWorklists)

const RecordManualPassageWorklistPreview = ({
  className,
  worklists,
  plates,
}: RecordManualPassageWorklistPreviewMonitorProps) => {
  const platesByBarcode = keyBy('user_facing_id', plates)
  const previewWorklists = _getPreviewWorklists(worklists)

  const tableColumns: TableColumn<RecordManualPassagePreviewWorklistMonitorProps>[] = [
    {
      name: 'Parent Plate',
      width: 120,
      render: (plate: RecordManualPassagePreviewWorklistMonitorProps) => (
        <div className={cs.plate}>
          <TinyMicroplate
            plateFormat={
              (platesByBarcode[plate.parentPlateBarcode] as any).plate_format
            }
            size='medium'
            highlights={[
              {
                color: 'accent',
                fn: (row, col) => {
                  const wellName = convertWellCoordsToWellName(row, col)
                  return plate.parentWells.includes(wellName)
                },
              },
            ]}
          />
          <div className={cs.plateName}>{plate.parentPlateBarcode}</div>
        </div>
      ),
    },
    {
      name: '',
      width: 120,
      render: () => (
        <div className={cs.pointerContainer}>
          <RightPointerIcon className={cs.pointer} />
        </div>
      ),
    },
    {
      name: 'Plate',
      width: 120,
      render: (plate: RecordManualPassagePreviewWorklistMonitorProps) => (
        <div>
          <TinyMicroplate
            plateFormat={(platesByBarcode[plate.plateBarcode] as any).plate_format}
            size='medium'
            highlights={[
              {
                color: 'accent',
                fn: (row, col) => {
                  const wellName = convertWellCoordsToWellName(row, col)
                  return plate.wells.includes(wellName)
                },
              },
            ]}
          />
          <div className={cs.plateName}>{plate.plateBarcode}</div>
        </div>
      ),
    },
  ]

  return (
    <div className={cx(className, cs.recordManualPassageWorklistPreview)}>
      <Table
        className={cx(className, cs.recordManualPassageWorklistTable)}
        columns={tableColumns}
        data={previewWorklists}
        rowKey='id'
        rowLeftRightPaddingVariant='rowPaddingLow'
        heightSizing='flexAuto'
      />
    </div>
  )
}

export default RecordManualPassageWorklistPreview
